export interface ThemeColors {
  '--theme-background': string;
  '--theme-primary': string;
  '--theme-primary-foreground': string;
  '--theme-accent': string;
  '--theme-accent-foreground': string;
  '--theme-sidebar': string;
  '--theme-card-border': string;
}

export interface ThemeDefinition {
  id: string;
  name: string;
  emoji: string;
  light: ThemeColors;
  dark: ThemeColors;
  premium?: boolean;
  mode: 'light' | 'dark';
}

// ============================================================================
// 16 Curated Palettes — 8 Light + 8 Dark
// ============================================================================

export const themes: ThemeDefinition[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // LIGHT MODE (8)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'default',
    name: 'コットン',
    emoji: '💎',
    mode: 'light',
    light: {
      '--theme-background': '0 0% 99%',
      '--theme-primary': '243 75% 59%',
      '--theme-primary-foreground': '210 40% 98%',
      '--theme-accent': '210 40% 95%',
      '--theme-accent-foreground': '222.2 84% 4.9%',
      '--theme-sidebar': '0 0% 99%',
      '--theme-card-border': '214.3 31.8% 89%',
    },
    dark: {
      '--theme-background': '222.2 84% 4.9%',
      '--theme-primary': '243 75% 59%',
      '--theme-primary-foreground': '210 40% 98%',
      '--theme-accent': '217.2 32.6% 17.5%',
      '--theme-accent-foreground': '210 40% 98%',
      '--theme-sidebar': '222.2 84% 4.9%',
      '--theme-card-border': '217.2 32.6% 17.5%',
    },
  },
  {
    id: 'light-sakura',
    name: '桜',
    emoji: '🌸',
    mode: 'light',
    premium: true,
    light: {
      '--theme-background': '340 35% 94%',
      '--theme-primary': '340 65% 58%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '340 50% 91%',
      '--theme-accent-foreground': '340 60% 32%',
      '--theme-sidebar': '340 30% 95%',
      '--theme-card-border': '340 35% 86%',
    },
    dark: {
      '--theme-background': '340 35% 7%',
      '--theme-primary': '340 65% 65%',
      '--theme-primary-foreground': '340 60% 8%',
      '--theme-accent': '340 30% 15%',
      '--theme-accent-foreground': '340 60% 82%',
      '--theme-sidebar': '340 30% 8%',
      '--theme-card-border': '340 20% 22%',
    },
  },
  {
    id: 'light-mint',
    name: 'ミント',
    emoji: '🍃',
    mode: 'light',
    premium: true,
    light: {
      '--theme-background': '162 30% 94%',
      '--theme-primary': '162 50% 42%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '162 40% 90%',
      '--theme-accent-foreground': '162 50% 24%',
      '--theme-sidebar': '162 22% 95%',
      '--theme-card-border': '162 28% 83%',
    },
    dark: {
      '--theme-background': '162 30% 7%',
      '--theme-primary': '162 50% 55%',
      '--theme-primary-foreground': '162 50% 8%',
      '--theme-accent': '162 25% 14%',
      '--theme-accent-foreground': '162 50% 72%',
      '--theme-sidebar': '162 25% 8%',
      '--theme-card-border': '162 18% 20%',
    },
  },
  {
    id: 'light-lavender',
    name: 'ラベンダー',
    emoji: '💜',
    mode: 'light',
    premium: true,
    light: {
      '--theme-background': '265 28% 94%',
      '--theme-primary': '265 50% 56%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '265 40% 91%',
      '--theme-accent-foreground': '265 50% 32%',
      '--theme-sidebar': '265 20% 95%',
      '--theme-card-border': '265 25% 85%',
    },
    dark: {
      '--theme-background': '265 30% 7%',
      '--theme-primary': '265 50% 65%',
      '--theme-primary-foreground': '265 50% 8%',
      '--theme-accent': '265 25% 15%',
      '--theme-accent-foreground': '265 50% 78%',
      '--theme-sidebar': '265 25% 8%',
      '--theme-card-border': '265 18% 22%',
    },
  },
  {
    id: 'light-peach',
    name: 'ピーチ',
    emoji: '🍑',
    mode: 'light',
    premium: true,
    light: {
      '--theme-background': '15 40% 94%',
      '--theme-primary': '15 70% 52%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '15 55% 91%',
      '--theme-accent-foreground': '15 65% 30%',
      '--theme-sidebar': '15 30% 95%',
      '--theme-card-border': '15 38% 85%',
    },
    dark: {
      '--theme-background': '15 35% 7%',
      '--theme-primary': '15 70% 58%',
      '--theme-primary-foreground': '15 70% 8%',
      '--theme-accent': '15 35% 14%',
      '--theme-accent-foreground': '15 70% 75%',
      '--theme-sidebar': '15 30% 8%',
      '--theme-card-border': '15 22% 20%',
    },
  },
  {
    id: 'light-sky',
    name: 'スカイ',
    emoji: '☁️',
    mode: 'light',
    premium: true,
    light: {
      '--theme-background': '200 35% 94%',
      '--theme-primary': '200 75% 46%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '200 55% 91%',
      '--theme-accent-foreground': '200 75% 26%',
      '--theme-sidebar': '200 28% 95%',
      '--theme-card-border': '200 38% 84%',
    },
    dark: {
      '--theme-background': '200 40% 7%',
      '--theme-primary': '200 75% 55%',
      '--theme-primary-foreground': '200 75% 8%',
      '--theme-accent': '200 40% 14%',
      '--theme-accent-foreground': '200 75% 78%',
      '--theme-sidebar': '200 35% 8%',
      '--theme-card-border': '200 25% 20%',
    },
  },
  {
    id: 'light-honey',
    name: 'ハニー',
    emoji: '🍯',
    mode: 'light',
    premium: true,
    light: {
      '--theme-background': '38 38% 94%',
      '--theme-primary': '38 80% 48%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '38 55% 90%',
      '--theme-accent-foreground': '38 75% 26%',
      '--theme-sidebar': '38 28% 95%',
      '--theme-card-border': '38 38% 83%',
    },
    dark: {
      '--theme-background': '38 35% 7%',
      '--theme-primary': '38 80% 55%',
      '--theme-primary-foreground': '38 80% 8%',
      '--theme-accent': '38 40% 14%',
      '--theme-accent-foreground': '38 80% 72%',
      '--theme-sidebar': '38 30% 8%',
      '--theme-card-border': '38 22% 20%',
    },
  },

  {
    id: 'light-slate',
    name: 'スレート',
    emoji: '🪨',
    mode: 'light',
    premium: true,
    light: {
      '--theme-background': '210 8% 95%',
      '--theme-primary': '210 10% 42%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '210 6% 91%',
      '--theme-accent-foreground': '210 10% 24%',
      '--theme-sidebar': '210 6% 96%',
      '--theme-card-border': '210 8% 85%',
    },
    dark: {
      '--theme-background': '210 8% 8%',
      '--theme-primary': '210 10% 58%',
      '--theme-primary-foreground': '210 8% 8%',
      '--theme-accent': '210 6% 15%',
      '--theme-accent-foreground': '210 8% 76%',
      '--theme-sidebar': '210 6% 6%',
      '--theme-card-border': '210 6% 20%',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DARK MODE (8)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'dark-default',
    name: 'オニキス',
    emoji: '🖤',
    mode: 'dark',
    light: {
      '--theme-background': '0 0% 99%',
      '--theme-primary': '243 75% 59%',
      '--theme-primary-foreground': '210 40% 98%',
      '--theme-accent': '210 40% 95%',
      '--theme-accent-foreground': '222.2 84% 4.9%',
      '--theme-sidebar': '0 0% 99%',
      '--theme-card-border': '214.3 31.8% 89%',
    },
    dark: {
      '--theme-background': '220 30% 8%',
      '--theme-primary': '243 75% 65%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '220 30% 14%',
      '--theme-accent-foreground': '210 40% 96%',
      '--theme-sidebar': '220 40% 6%',
      '--theme-card-border': '220 25% 18%',
    },
  },
  {
    id: 'dark-midnight',
    name: 'ミッドナイト',
    emoji: '🌙',
    mode: 'dark',
    premium: true,
    light: {
      '--theme-background': '215 35% 94%',
      '--theme-primary': '215 85% 50%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '215 55% 91%',
      '--theme-accent-foreground': '215 85% 28%',
      '--theme-sidebar': '215 25% 95%',
      '--theme-card-border': '215 38% 83%',
    },
    dark: {
      '--theme-background': '215 55% 7%',
      '--theme-primary': '215 85% 60%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '215 50% 12%',
      '--theme-accent-foreground': '215 80% 82%',
      '--theme-sidebar': '215 55% 6%',
      '--theme-card-border': '215 35% 18%',
    },
  },
  {
    id: 'dark-forest',
    name: 'フォレスト',
    emoji: '🌲',
    mode: 'dark',
    premium: true,
    light: {
      '--theme-background': '155 25% 94%',
      '--theme-primary': '155 65% 38%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '155 45% 90%',
      '--theme-accent-foreground': '155 65% 22%',
      '--theme-sidebar': '155 18% 95%',
      '--theme-card-border': '155 28% 82%',
    },
    dark: {
      '--theme-background': '155 45% 7%',
      '--theme-primary': '155 65% 50%',
      '--theme-primary-foreground': '155 65% 6%',
      '--theme-accent': '155 40% 13%',
      '--theme-accent-foreground': '155 60% 72%',
      '--theme-sidebar': '155 45% 5%',
      '--theme-card-border': '155 30% 18%',
    },
  },
  {
    id: 'dark-wine',
    name: 'ワイン',
    emoji: '🍷',
    mode: 'dark',
    premium: true,
    light: {
      '--theme-background': '345 30% 94%',
      '--theme-primary': '345 70% 48%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '345 50% 91%',
      '--theme-accent-foreground': '345 70% 30%',
      '--theme-sidebar': '345 22% 95%',
      '--theme-card-border': '345 32% 84%',
    },
    dark: {
      '--theme-background': '345 40% 7%',
      '--theme-primary': '345 70% 58%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '345 40% 13%',
      '--theme-accent-foreground': '345 65% 80%',
      '--theme-sidebar': '345 45% 6%',
      '--theme-card-border': '345 28% 18%',
    },
  },
  {
    id: 'dark-amethyst',
    name: 'アメジスト',
    emoji: '🔮',
    mode: 'dark',
    premium: true,
    light: {
      '--theme-background': '270 25% 94%',
      '--theme-primary': '270 60% 52%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '270 45% 91%',
      '--theme-accent-foreground': '270 60% 30%',
      '--theme-sidebar': '270 18% 95%',
      '--theme-card-border': '270 28% 83%',
    },
    dark: {
      '--theme-background': '270 40% 7%',
      '--theme-primary': '270 60% 65%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '270 35% 13%',
      '--theme-accent-foreground': '270 55% 80%',
      '--theme-sidebar': '270 40% 6%',
      '--theme-card-border': '270 25% 18%',
    },
  },
  {
    id: 'dark-ocean',
    name: 'ディープシー',
    emoji: '🌊',
    mode: 'dark',
    premium: true,
    light: {
      '--theme-background': '185 30% 94%',
      '--theme-primary': '185 70% 40%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '185 50% 91%',
      '--theme-accent-foreground': '185 70% 24%',
      '--theme-sidebar': '185 22% 95%',
      '--theme-card-border': '185 32% 83%',
    },
    dark: {
      '--theme-background': '185 45% 7%',
      '--theme-primary': '185 70% 52%',
      '--theme-primary-foreground': '185 70% 6%',
      '--theme-accent': '185 40% 13%',
      '--theme-accent-foreground': '185 65% 75%',
      '--theme-sidebar': '185 45% 5%',
      '--theme-card-border': '185 28% 18%',
    },
  },
  {
    id: 'dark-ember',
    name: 'エンバー',
    emoji: '🔥',
    mode: 'dark',
    premium: true,
    light: {
      '--theme-background': '25 35% 94%',
      '--theme-primary': '25 80% 50%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '25 55% 91%',
      '--theme-accent-foreground': '25 80% 28%',
      '--theme-sidebar': '25 25% 95%',
      '--theme-card-border': '25 38% 84%',
    },
    dark: {
      '--theme-background': '25 45% 7%',
      '--theme-primary': '25 80% 55%',
      '--theme-primary-foreground': '25 80% 6%',
      '--theme-accent': '25 45% 13%',
      '--theme-accent-foreground': '25 75% 75%',
      '--theme-sidebar': '25 50% 5%',
      '--theme-card-border': '25 30% 18%',
    },
  },
  {
    id: 'dark-graphite',
    name: 'グラファイト',
    emoji: '⚙️',
    mode: 'dark',
    premium: true,
    light: {
      '--theme-background': '220 5% 94%',
      '--theme-primary': '220 6% 42%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '220 4% 90%',
      '--theme-accent-foreground': '220 6% 24%',
      '--theme-sidebar': '220 4% 95%',
      '--theme-card-border': '220 5% 83%',
    },
    dark: {
      '--theme-background': '220 6% 8%',
      '--theme-primary': '220 6% 55%',
      '--theme-primary-foreground': '0 0% 100%',
      '--theme-accent': '220 5% 14%',
      '--theme-accent-foreground': '220 5% 76%',
      '--theme-sidebar': '220 6% 6%',
      '--theme-card-border': '220 5% 19%',
    },
  },
];

export function getThemeById(id: string): ThemeDefinition | undefined {
  return themes.find(t => t.id === id);
}

export function getThemesForMode(mode: 'light' | 'dark'): ThemeDefinition[] {
  return themes.filter(t => t.mode === mode);
}
